import { Modal } from "antd";
import { useState } from "react";
import { PiGooglePhotosLogo } from "react-icons/pi";
import { EIntegrationPlatform } from "../../models/integrations.model"; // Adjust path
import { TInterest } from "../../models/interests.model"; // Adjust path
import { TPost } from "../../models/posts.model"; // Adjust path
import { useTheme } from "../../providers/ThemeProvider"; // Adjust path
import { TResponse } from "../../services/apiWorker"; // Adjust path
import { deletePost } from "../../services/posts.service";
import { formatDate } from "../../utils/date";
import { Card } from "../card"; // Adjust path
import { ModalPostEdit } from "./ModalPostEdit"; // Adjust path
import ModalPreviewEvent from "./chart/ModalPreviewEvent";

export function TimelineCard({
  card,
  model = "OLD",
  interests,
  allowOptions = true,
}: {
  model?: "OLD" | "NEW";
  card: TPost;
  interests: TResponse<TInterest>;
  allowOptions?: boolean;
}) {
  const { message } = useTheme();
  const [isEditing, setEditing] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [isPreviewing, setIsPreviewing] = useState<boolean>(false);

  const content =
    typeof card.content == "object"
      ? card.content
      : JSON.parse(card.content || "{}");

  const isText = "text" in content;
  const hasIntegration = card.integrationId && "integrationStore" in card;

  let cardIcon = undefined;
  let cardTitle = formatDate(new Date(card.eventDate || card.createdAt));

  if (
    hasIntegration &&
    card.integrationStore?.platform == EIntegrationPlatform.GOOGLE_PHOTOS
  ) {
    cardIcon = <PiGooglePhotosLogo />;
  }

  const handleOpenPreview = () => {
    setIsPreviewing(true);
  };

  const handleClosePreview = () => {
    setIsPreviewing(false);
  };

  const handleEditFromPreview = () => {
    setIsPreviewing(false);
    setEditing(true);
  };

  const handleAddToTimeline = () => {
    console.log("Adding event to timeline:", card.id);
    message.success(
      `Event "${card.title || "Untitled"}" added to timeline (simulation).`
    );
    setIsPreviewing(false);
  };

  const handleDeleteConfirm = async () => {
    console.log("Deleting event:", card.id);
    message.loading({ content: "Deleting event...", key: "deleteEvent" });

    deletePost({
      postId: card.id,
      onSuccess: () => {
        message.success({
          content: "Event deleted successfully!",
          key: "deleteEvent",
          duration: 2,
        });
        setDeleting(false);
      },
      onError: (data) => {
        console.error("Failed to delete event:", data);
        message.error({
          content: "Failed to delete event.",
          key: "deleteEvent",
          duration: 2,
        });
        setDeleting(false);
      },
    });
  };

  return (
    <>
      {isEditing && (
        <ModalPostEdit
          onClose={() => setEditing(false)}
          onUpdate={() => {
            setEditing(false);
          }}
          post={card}
        />
      )}

      <ModalPreviewEvent
        open={isPreviewing}
        onClose={handleClosePreview}
        onEdit={handleEditFromPreview}
        onAddToTimeline={handleAddToTimeline}
        event={card}
        interestsData={interests}
      />

      <Modal
        title="Confirm Deletion"
        open={isDeleting}
        onOk={handleDeleteConfirm}
        confirmLoading={false}
        onCancel={() => setDeleting(false)}
        okText="Delete"
        okType="danger"
        centered
      >
        <p>Are you sure you want to permanently delete this event?</p>
        {card.title && (
          <p>
            <strong>{card.title}</strong>
          </p>
        )}
      </Modal>

      <div
        onClick={handleOpenPreview}
        style={{ cursor: "pointer" }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleOpenPreview()}
      >
        <Card
          key={card.id}
          modelStyle={model}
          model={{
            featuredImage: card.medias?.length === 1 && card.medias[0] != null,
            gallery: card.medias?.length > 1,
            title: !!card.title,
            description: isText ? content.text : undefined,
            interests: card.interests?.length > 0,
          }}
          title={card.title}
          description={isText ? content.text : undefined}
          featuredGallery={card.medias}
          featuredImage={card.medias?.[0]}
          interests={card.interests
            ?.map((interestId) =>
              interests.content.find((i) => i.id === interestId)
            )
            .filter((interest): interest is TInterest => !!interest)}
          cardIcon={cardIcon}
          cardTitle={cardTitle}
          onEdit={!allowOptions ? undefined : () => setEditing(true)}
          onDelete={!allowOptions ? undefined : () => setDeleting(true)}
        />
      </div>
    </>
  );
}
